import { SALON_SHOWCASE_VERSION, SHOWCASE_SCHEMA_VERSION } from '../application/contracts.js';
import type { AutoDriveBackupStatus } from './auto-drive-backup.js';
import type { BackupStatus } from './backup.js';
import type { LocalStorageStatus } from './global.js';
import { DB_NAME, DB_VERSION } from './indexeddb-store.js';

export const PWA_CANDIDATE = 'Browser/PWA RC3' as const;

export interface ServiceWorkerMaintenanceStatus {
  supported: boolean;
  registration: 'unsupported' | 'not_registered' | 'installing' | 'waiting' | 'active';
  controlled: boolean;
  updateWaiting: boolean;
  scope: string | null;
}

export interface MaintenanceSummary {
  format: 'calcomate-salon-installation-maintenance';
  formatVersion: 1;
  generatedAt: string;
  product: { name: 'Calcomate Salon'; version: string; schemaVersion: number; candidate: typeof PWA_CANDIDATE };
  origin: string;
  localStorage: {
    kind: 'IndexedDB'; databaseName: string; databaseVersion: number;
    persistedProtectionGranted: boolean; writerTab: boolean; usageBytes: number | null; quotaBytes: number | null;
    filesystemPath: null;
  };
  backup: {
    packageFormat: 'encrypted .calsalon'; automaticMode: 'enabled' | 'disabled';
    lastAttemptAt: string | null; lastVerifiedDriveBackupAt: string | null; nextAttemptAt: string | null;
    pending: boolean; running: boolean; errorCode: string | null;
    browserClosedGuarantee: false; requiresSessionPassphrase: true; requiresValidGoogleAuthorization: true;
  };
  googleDrive: {
    connectedForThisSession: boolean; account: null; target: string | null;
    note: string;
  };
  update: ServiceWorkerMaintenanceStatus;
  recoverySteps: string[];
}

export interface MaintenanceSummaryInput {
  generatedAt: string;
  storage: LocalStorageStatus;
  backup: BackupStatus;
  automatic: AutoDriveBackupStatus;
  update: ServiceWorkerMaintenanceStatus;
}

export interface MaintenanceDownload { fileName: 'KURULUM-VE-BAKIM.md' | 'KURULUM-VE-BAKIM.json'; mimeType: string; text: string; }

export function createMaintenanceSummary(input: MaintenanceSummaryInput): MaintenanceSummary {
  return {
    format: 'calcomate-salon-installation-maintenance',
    formatVersion: 1,
    generatedAt: input.generatedAt,
    product: { name: 'Calcomate Salon', version: SALON_SHOWCASE_VERSION, schemaVersion: SHOWCASE_SCHEMA_VERSION, candidate: PWA_CANDIDATE },
    origin: input.storage.origin,
    localStorage: {
      kind: 'IndexedDB', databaseName: DB_NAME, databaseVersion: DB_VERSION,
      persistedProtectionGranted: input.storage.persisted, writerTab: input.storage.writer,
      usageBytes: input.storage.usage, quotaBytes: input.storage.quota, filesystemPath: null
    },
    backup: {
      packageFormat: 'encrypted .calsalon', automaticMode: input.automatic.enabled ? 'enabled' : 'disabled',
      lastAttemptAt: input.automatic.lastAttemptAt,
      lastVerifiedDriveBackupAt: input.backup.lastDriveAt,
      nextAttemptAt: input.automatic.nextAttemptAt,
      pending: input.automatic.pending, running: input.automatic.running,
      errorCode: input.automatic.error?.code ?? input.backup.retentionError?.code ?? null,
      browserClosedGuarantee: false, requiresSessionPassphrase: true, requiresValidGoogleAuthorization: true
    },
    googleDrive: {
      connectedForThisSession: input.automatic.connected,
      account: null,
      target: input.automatic.connected ? 'drive.file kapsamındaki uygulama tarafından oluşturulmuş şifreli yedek dosyaları' : null,
      note: 'Uygulama Google hesap kimliğini veya hedef klasör bilgisini saklamaz; bilinmeyen değerler uydurulmaz.'
    },
    update: input.update,
    recoverySteps: [
      'Eski cihazda Ayarlar > Yedek & Geri Yükle bölümünden güncel bir şifreli .calsalon yedeği alın.',
      'Yeni cihazda uygulamayı bu özette yazan aynı HTTPS origin üzerinden açın.',
      'İlk kurulum ekranından yedek dosyasını seçin ve yalnız sizin bildiğiniz yedek parolasını girin.',
      'Müşteri, randevu, satış, ödeme, stok ve fotoğrafları kontrol edin; ardından yeni bir işlem oluşturup yeniden açılışta korunduğunu doğrulayın.',
      'Drive kullanılacaksa Google hesabını yeniden bağlayın ve otomatik yedek için parolayı bu açık oturumda yeniden girin.'
    ]
  };
}

const show = (value: string | number | boolean | null): string => value === null ? 'Bilinmiyor / yok' : String(value);

export function maintenanceSummaryMarkdown(summary: MaintenanceSummary): string {
  return `# Calcomate Salon — Kurulum ve Bakım Özeti

Bu dosya ${summary.generatedAt} tarihinde uygulamanın bildiği güncel durumdan yeniden üretildi. Parola, token, şifreleme anahtarı, müşteri kaydı veya fotoğraf içermez.

## Ürün ve kurulum

- Ürün: ${summary.product.name}
- Ürün sürümü: ${summary.product.version}
- Aday: ${summary.product.candidate}
- Veri şeması: ${summary.product.schemaVersion}
- Gerçek origin: ${summary.origin}
- Yerel depolama: ${summary.localStorage.kind} (${summary.localStorage.databaseName}, sürüm ${summary.localStorage.databaseVersion})
- Sabit PostgreSQL/veri klasörü yolu: Yok — PWA tarayıcı origin'ine bağlı IndexedDB kullanır
- Kalıcı depolama koruması: ${summary.localStorage.persistedProtectionGranted ? 'Verildi' : 'Verilmedi / bilinmiyor'}
- Bu sekme: ${summary.localStorage.writerTab ? 'Yazıcı' : 'Salt okunur'}

## Yedekleme

- Paket: ${summary.backup.packageFormat}
- Otomatik yedek: ${summary.backup.automaticMode === 'enabled' ? 'Etkin' : 'Kapalı'}
- Son deneme: ${show(summary.backup.lastAttemptAt)}
- Son doğrulanan Drive yedeği: ${show(summary.backup.lastVerifiedDriveBackupAt)}
- Sonraki deneme: ${show(summary.backup.nextAttemptAt)}
- Bekleyen iş: ${summary.backup.pending ? 'Var' : 'Yok'}
- Hata: ${show(summary.backup.errorCode)}
- Sınır: Tarayıcı kapalıyken çalışmaz; açık oturum parolası ve geçerli Google yetkisi gerekir

## Google Drive

- Bu oturumda bağlı: ${summary.googleDrive.connectedForThisSession ? 'Evet' : 'Hayır'}
- Hesap: Bilinmiyor — uygulama hesap kimliğini saklamaz
- Hedef: ${show(summary.googleDrive.target)}
- Not: ${summary.googleDrive.note}

## Güncelleme

- Service worker desteği: ${summary.update.supported ? 'Var' : 'Yok'}
- Durum: ${summary.update.registration}
- Sayfa service worker tarafından kontrol ediliyor: ${summary.update.controlled ? 'Evet' : 'Hayır'}
- Bekleyen güncelleme: ${summary.update.updateWaiting ? 'Var' : 'Yok'}
- Kapsam: ${show(summary.update.scope)}

## Kurtarma adımları

${summary.recoverySteps.map((step, index) => `${index + 1}. ${step}`).join('\n')}
`;
}

export function maintenanceDownload(summary: MaintenanceSummary, format: 'markdown' | 'json'): MaintenanceDownload {
  if (format === 'json') return { fileName: 'KURULUM-VE-BAKIM.json', mimeType: 'application/json;charset=utf-8', text: `${JSON.stringify(summary, null, 2)}\n` };
  return { fileName: 'KURULUM-VE-BAKIM.md', mimeType: 'text/markdown;charset=utf-8', text: maintenanceSummaryMarkdown(summary) };
}

export async function serviceWorkerMaintenanceStatus(): Promise<ServiceWorkerMaintenanceStatus> {
  if (!('serviceWorker' in navigator)) return { supported: false, registration: 'unsupported', controlled: false, updateWaiting: false, scope: null };
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return { supported: true, registration: 'not_registered', controlled: Boolean(navigator.serviceWorker.controller), updateWaiting: false, scope: null };
  return {
    supported: true,
    registration: registration.waiting ? 'waiting' : registration.installing ? 'installing' : registration.active ? 'active' : 'not_registered',
    controlled: Boolean(navigator.serviceWorker.controller),
    updateWaiting: Boolean(registration.waiting),
    scope: registration.scope
  };
}
